import { useEffect, useState } from "react";
import { Upload, FileSpreadsheet, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  apiErrorMessage,
  inputCls,
  useContextClientId,
  useHierarchy,
} from "./hierarchy";

/* Labels dos tipos canônicos (catálogo do módulo) para a pré-visualização. */
const TYPE_LABELS = {
  porta_isca: "Porta-isca",
  trex: "T-Rex",
  armadilha_mecanica: "Armadilha mecânica",
  armadilha_luminosa: "Armadilha luminosa",
  armadilha_biologica: "Armadilha biológica",
  placa_cola: "Placa de cola",
  catrex: "Catrex",
  outro: "Outro",
};

const STATUS_STYLE = {
  valid: "text-green-600",
  duplicate: "text-amber-600",
  ignored: "text-slate-500",
  invalid: "text-red-600",
};

const STATUS_LABEL = {
  valid: "VÁLIDO",
  duplicate: "DUPLICADO",
  ignored: "IGNORADO",
  invalid: "INVÁLIDO",
};

function rowReason(row) {
  if (row.errors && row.errors.length) return row.errors[0];
  if (row.reason === "DISPOSITIVO_EXISTENTE")
    return "Dispositivo já cadastrado.";
  if (row.reason) return row.reason;
  return "—";
}

export default function ImportXLSX() {
  const contextClientId = useContextClientId();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState("ignore");
  const [clientId, setClientId] = useState(contextClientId);
  const [unitId, setUnitId] = useState("");
  const { clients, units } = useHierarchy({ clientId });

  useEffect(() => {
    setClientId(contextClientId);
    setUnitId("");
  }, [contextClientId]);

  // Qualquer mudança de contexto invalida a pré-visualização.
  useEffect(() => {
    setPreview(null);
    setResult(null);
  }, [clientId, unitId, policy]);

  const upload = async (dryRun) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = { dry_run: dryRun, duplicate_policy: policy };
      if (clientId) params.default_client_id = clientId;
      if (unitId) params.default_unit_id = unitId;
      const r = await api.post("/device-monitoring/devices/import", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (dryRun) {
        setPreview(r.data);
        setResult(null);
        if (r.data.blocking) toast.error(r.data.blocking_message);
      } else {
        setResult(r.data);
        setPreview(null);
        const t = r.data.totals;
        toast.success(
          `Importação concluída: ${t.imported} criado(s), ${t.updated} atualizado(s), ${t.invalid} inválido(s).`
        );
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro na importação."));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/device-monitoring/import/template", {
        params: { format: "xlsx" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template-dispositivos.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Não foi possível baixar o template."));
    }
  };

  const t = preview?.totals;
  const previewRows = preview?.rows || [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        <h3 className="font-semibold">Importar dispositivos XLSX</h3>
        <p className="text-sm text-muted-foreground">
          Formato novo: Número, Código, Local, Área, Unidade, Tipo.{" "}
          <strong>
            Compatível também com planilhas legadas do cadastro de
            dispositivos
          </strong>{" "}
          (Filial, CodigoCliente, NomeCliente, TipoDispositivo,
          NumeroDispositivo, LocalInstalacao, ...) — o formato é detectado
          automaticamente pelos cabeçalhos.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl">
          <div>
            <label className="block text-sm mb-1">Cliente de destino</label>
            <select
              data-testid="import-client"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setUnitId("");
              }}
              className={inputCls}
            >
              <option value="">Todos os clientes (resolver pela planilha)</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Unidade de destino</label>
            <select
              data-testid="import-unit"
              value={unitId}
              disabled={!clientId}
              onChange={(e) => setUnitId(e.target.value)}
              className={inputCls}
            >
              <option value="">
                {clientId ? "Resolver pela planilha (Unidade/Filial)" : "Selecione um cliente"}
              </option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!clientId && (
          <p className="text-xs text-muted-foreground">
            Sem cliente selecionado, cada linha precisa identificar cliente e
            unidade (CNPJ/CPF, CodigoCliente, NomeCliente, Filial/Unidade).
            Linhas sem identificação bloqueiam a importação.
          </p>
        )}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={policy === "ignore"}
              onChange={() => setPolicy("ignore")}
            />
            Ignorar duplicados
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={policy === "update"}
              onChange={() => setPolicy("update")}
            />
            Atualizar duplicados
          </label>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-md cursor-pointer">
            <Upload className="h-4 w-4" />
            <span className="text-sm">Selecionar arquivo</span>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => {
                setFile(e.target.files[0]);
                setPreview(null);
                setResult(null);
              }}
              className="hidden"
            />
          </label>
          {file && <span className="text-sm">{file.name}</span>}
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <Download className="h-4 w-4" />
            Baixar template (formato novo)
          </button>
        </div>
        <div className="flex gap-2">
          <button
            disabled={!file || loading}
            onClick={() => upload(true)}
            className="px-4 py-2 bg-secondary rounded-md text-sm font-medium disabled:opacity-50"
          >
            Pré-visualizar
          </button>
          <button
            disabled={!file || loading || !preview || preview.blocking}
            data-testid="import-confirm"
            onClick={() => upload(false)}
            title={
              preview?.blocking
                ? preview.blocking_message
                : preview
                ? ""
                : "Faça a pré-visualização antes de importar"
            }
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
          >
            Importar
          </button>
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Pré-visualização —{" "}
            {preview.format === "legacy"
              ? "formato legado detectado"
              : "formato novo detectado"}
          </h4>
          {preview.blocking && (
            <div className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4" />
              {preview.blocking_message} ({t.unresolved} linha(s) sem cliente/unidade)
            </div>
          )}
          {preview.context?.client_name && (
            <p className="text-sm text-muted-foreground">
              Destino: <strong>{preview.context.client_name}</strong>
              {preview.context.unit_name ? ` / ${preview.context.unit_name}` : ""}
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
            <div>Total: {t.total_rows}</div>
            <div className="text-green-600">Novos: {t.valid}</div>
            <div className="text-blue-600">Atualizáveis: {t.updatable ?? 0}</div>
            <div className="text-amber-600">Duplicados: {t.duplicates}</div>
            <div className="text-slate-500">Ignorados: {t.ignored}</div>
            <div className="text-red-600">Inválidos: {t.invalid}</div>
          </div>

          <div className="overflow-auto max-h-[24rem] border border-border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                <tr>
                  <th className="text-left py-2 px-2">Linha</th>
                  <th className="text-left py-2 px-2">Número</th>
                  <th className="text-left py-2 px-2">Código</th>
                  <th className="text-left py-2 px-2">Tipo original</th>
                  <th className="text-left py-2 px-2">Tipo reconhecido</th>
                  <th className="text-left py-2 px-2">Área</th>
                  <th className="text-left py-2 px-2">Local</th>
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-left py-2 px-2">Unidade</th>
                  <th className="text-left py-2 px-2">Situação</th>
                  <th className="text-left py-2 px-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 200).map((r) => (
                  <tr key={r.row} className="border-b border-border/50">
                    <td className="py-1.5 px-2">{r.row}</td>
                    <td className="py-1.5 px-2 font-mono">{r.number || "—"}</td>
                    <td className="py-1.5 px-2 font-mono">{r.code || "—"}</td>
                    <td className="py-1.5 px-2">{r.original_type || "—"}</td>
                    <td className="py-1.5 px-2">
                      {r.type_key ? TYPE_LABELS[r.type_key] || r.type_key : "—"}
                    </td>
                    <td className="py-1.5 px-2">{r.area || "—"}</td>
                    <td className="py-1.5 px-2">{r.location || "—"}</td>
                    <td className="py-1.5 px-2">{r.client || "—"}</td>
                    <td className="py-1.5 px-2">{r.unit || "—"}</td>
                    <td
                      className={`py-1.5 px-2 font-medium ${STATUS_STYLE[r.status]}`}
                    >
                      {STATUS_LABEL[r.status] || r.status}
                    </td>
                    <td className="py-1.5 px-2">{rowReason(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewRows.length > 200 && (
            <p className="text-xs text-muted-foreground">
              Mostrando 200 de {previewRows.length} linhas.
            </p>
          )}
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h4 className="font-medium">Resultado</h4>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
            <div className="text-green-600">
              Importados: {result.totals.imported}
            </div>
            <div className="text-blue-600">
              Atualizados: {result.totals.updated}
            </div>
            <div className="text-slate-500">
              Ignorados: {result.totals.ignored}
            </div>
            <div className="text-amber-600">
              Duplicados: {result.totals.duplicates}
            </div>
            <div className="text-red-600">
              Inválidos: {result.totals.invalid}
            </div>
            <div>Total: {result.totals.total_rows}</div>
          </div>
          {(result.rows || []).some((r) => r.status === "invalid") && (
            <div className="overflow-auto max-h-64 border border-border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-2">Linha</th>
                    <th className="text-left py-2 px-2">Código</th>
                    <th className="text-left py-2 px-2">Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows
                    .filter((r) => r.status === "invalid")
                    .map((r) => (
                      <tr key={r.row} className="border-b border-border/50 text-red-600">
                        <td className="py-1.5 px-2">{r.row}</td>
                        <td className="py-1.5 px-2 font-mono">{r.code || "—"}</td>
                        <td className="py-1.5 px-2">{rowReason(r)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
